import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Edit3, RefreshCcw, Search, Store, Gift,
  Percent, TrendingUp, Users, CheckCircle2, AlertTriangle, IndianRupee, ExternalLink, Copy,
} from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const REWARD_TYPES = ["custom", "coins", "popsicle", "popcorn", "discount", "bogo", "mystery", "festival"];

export default function AdminPayScratch() {
  const [tab, setTab] = useState("dashboard"); // dashboard | outlets | rewards | payments | settings
  const [config, setConfig] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [payments, setPayments] = useState([]);
  const [probCheck, setProbCheck] = useState(null);
  const [q, setQ] = useState("");

  // Modals
  const [outletForm, setOutletForm] = useState(null);
  const [rewardForm, setRewardForm] = useState(null);

  const loadAll = async () => {
    try {
      const [c, a, o, r, p, pc] = await Promise.all([
        api.get("/admin/pay-scratch/config"),
        api.get("/admin/pay-scratch/analytics"),
        api.get("/admin/pay-scratch/outlets"),
        api.get("/admin/pay-scratch/rewards"),
        api.get("/admin/pay-scratch/payments"),
        api.get("/admin/pay-scratch/probability-check"),
      ]);
      setConfig(c.data); setAnalytics(a.data); setOutlets(o.data); setRewards(r.data); setPayments(p.data); setProbCheck(pc.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); }, []);

  const saveConfig = async () => {
    try {
      await api.put("/admin/pay-scratch/config", {
        is_enabled: !!config.is_enabled,
        better_luck_probability: parseFloat(config.better_luck_probability) || 0,
        better_luck_message: config.better_luck_message || "Better Luck Next Time",
        header_title: config.header_title || "Pay & Scratch",
        header_subtitle: config.header_subtitle || "",
        guest_token_ttl_days: parseInt(config.guest_token_ttl_days) || 30,
        coin_credit_on_win: !!config.coin_credit_on_win,
      });
      toast.success("Settings saved");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveOutlet = async () => {
    try {
      const payload = { name: outletForm.name, code: outletForm.code, address: outletForm.address || "", is_active: !!outletForm.is_active };
      if (outletForm.id) await api.put(`/admin/pay-scratch/outlets/${outletForm.id}`, payload);
      else await api.post("/admin/pay-scratch/outlets", payload);
      toast.success("Outlet saved");
      setOutletForm(null); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delOutlet = async (o) => {
    if (!window.confirm(`Delete outlet "${o.name}"?`)) return;
    try { await api.delete(`/admin/pay-scratch/outlets/${o.id}`); toast.success("Deleted"); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveReward = async () => {
    try {
      const payload = {
        name: rewardForm.name, description: rewardForm.description || "",
        image_url: rewardForm.image_url || null, type: rewardForm.type || "custom",
        value: rewardForm.value || null,
        probability: parseFloat(rewardForm.probability) || 0,
        quantity_cap: parseInt(rewardForm.quantity_cap) || 0,
        expiry_days: parseInt(rewardForm.expiry_days) || 30,
        is_active: !!rewardForm.is_active,
        campaign_start: rewardForm.campaign_start || null,
        campaign_end: rewardForm.campaign_end || null,
        sort_order: parseInt(rewardForm.sort_order) || 0,
      };
      if (rewardForm.id) await api.put(`/admin/pay-scratch/rewards/${rewardForm.id}`, payload);
      else await api.post("/admin/pay-scratch/rewards", payload);
      toast.success("Reward saved");
      setRewardForm(null); loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const delReward = async (r) => {
    if (!window.confirm(`Delete reward "${r.name}"?`)) return;
    try { await api.delete(`/admin/pay-scratch/rewards/${r.id}`); toast.success("Deleted"); loadAll(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyQrLink = (code) => {
    const url = `${window.location.origin}/scratch/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("QR link copied");
  };

  const filteredPayments = payments.filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (p.receipt_number || "").toLowerCase().includes(s)
      || (p.razorpay_payment_id || "").toLowerCase().includes(s)
      || (p.customer_name || "").toLowerCase().includes(s)
      || (p.customer_phone || "").toLowerCase().includes(s)
      || (p.member_code || "").toLowerCase().includes(s)
      || (p.outlet_name || "").toLowerCase().includes(s);
  });

  if (!config) return <div>Loading…</div>;

  return (
    <div data-testid="admin-pay-scratch-page">
      <header className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Payments</div>
          <h1 className="editorial-h1 text-4xl">Pay &amp; Scratch</h1>
        </div>
        <button onClick={loadAll} className="btn-pill btn-ghost text-xs" data-testid="ps-refresh"><RefreshCcw size={11} /> Refresh</button>
      </header>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 flex-wrap">
        {[
          { k: "dashboard", l: "Dashboard" },
          { k: "outlets", l: "Outlets" },
          { k: "rewards", l: "Rewards & Probability" },
          { k: "payments", l: "Payments" },
          { k: "settings", l: "Settings" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ps-tab-${t.k}`}
            className={`btn-pill text-xs ${tab === t.k ? "btn-dark" : "btn-ghost"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "dashboard" && (
        <div>
          {/* Analytics */}
          {analytics && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <Stat icon={IndianRupee} label="Revenue" value={`₹${analytics.total_revenue.toLocaleString()}`} accent />
              <Stat icon={CheckCircle2} label="Payments" value={analytics.total_payments} />
              <Stat icon={Gift} label="Scratch Cards" value={analytics.total_cards} />
              <Stat icon={TrendingUp} label="Wins" value={`${analytics.total_wins} (${analytics.win_rate}%)`} />
              <Stat icon={Users} label="Guest→Member" value={analytics.guest_conversions} />
              <Stat icon={CheckCircle2} label="Save Rate" value={`${analytics.save_rate}%`} />
            </div>
          )}
          {/* Reward distribution */}
          {analytics?.by_reward?.length > 0 && (
            <div className="rounded-2xl bg-white border border-brand-line p-5 mb-6">
              <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-3">Reward distribution</div>
              <ul className="space-y-2">
                {analytics.by_reward.map((r, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="text-brand-charcoal/80">{r.name}</span>
                    <span className="font-mono">{r.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Trend */}
          {analytics?.trend?.length > 0 && (
            <div className="rounded-2xl bg-white border border-brand-line p-5">
              <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55 mb-3">Payments — last 14 days</div>
              <div className="flex items-end gap-1 h-32">
                {analytics.trend.map((d, i) => {
                  const max = Math.max(1, ...analytics.trend.map((x) => x.count));
                  const h = Math.round((d.count / max) * 100);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end">
                      <div className="w-full rounded-t bg-brand-yellow" style={{ height: `${h}%`, minHeight: 2 }} title={`${d.count} payments`} />
                      <div className="text-[8px] mt-1 text-brand-charcoal/50">{d.date.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "outlets" && (
        <div className="rounded-2xl bg-white border border-brand-line p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Outlets</div>
            <button onClick={() => setOutletForm({ name: "", code: "", address: "", is_active: true })} className="btn-pill btn-dark text-xs" data-testid="ps-new-outlet"><Plus size={11} /> New Outlet</button>
          </div>
          {outlets.length === 0 ? (
            <div className="text-center text-brand-charcoal/50 py-8">No outlets yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">
                <tr>
                  <th className="text-left py-2">Name</th>
                  <th className="text-left py-2">Code</th>
                  <th className="text-left py-2">QR Link</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {outlets.map((o) => (
                  <tr key={o.id} className="border-t border-brand-line" data-testid={`ps-outlet-${o.id}`}>
                    <td className="py-2">
                      <div className="font-medium">{o.name}</div>
                      {o.address && <div className="text-xs text-brand-charcoal/60">{o.address}</div>}
                    </td>
                    <td className="py-2 font-mono text-xs">{o.code}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <a href={`/scratch/${o.code}`} target="_blank" rel="noreferrer" className="text-xs text-brand-charcoal hover:underline inline-flex items-center gap-1"><ExternalLink size={10} /> /scratch/{o.code}</a>
                        <button onClick={() => copyQrLink(o.code)} className="text-xs text-brand-charcoal/60 hover:text-brand-charcoal inline-flex items-center gap-1" data-testid={`ps-copy-${o.id}`}><Copy size={10} /></button>
                      </div>
                    </td>
                    <td className="py-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${o.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{o.is_active ? "Active" : "Inactive"}</span>
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => setOutletForm({ ...o })} className="text-xs mr-2 text-brand-charcoal/70 hover:text-brand-charcoal inline-flex items-center gap-1" data-testid={`ps-edit-outlet-${o.id}`}><Edit3 size={10} /> Edit</button>
                      <button onClick={() => delOutlet(o)} className="text-xs text-red-600 inline-flex items-center gap-1" data-testid={`ps-delete-outlet-${o.id}`}><Trash2 size={10} /> Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "rewards" && (
        <div>
          {/* Probability validator */}
          {probCheck && (
            <div className={`rounded-2xl border p-4 mb-4 ${probCheck.is_valid ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`} data-testid="ps-prob-status">
              <div className="flex items-center gap-2">
                {probCheck.is_valid ? <CheckCircle2 size={16} className="text-emerald-600" /> : <AlertTriangle size={16} className="text-red-600" />}
                <span className="text-sm font-medium">Probability validation</span>
              </div>
              <div className="mt-2 text-xs text-brand-charcoal/70">
                Rewards: <span className="font-mono">{probCheck.reward_probability_total.toFixed(1)}%</span>
                {" + "}Better Luck: <span className="font-mono">{probCheck.better_luck_probability.toFixed(1)}%</span>
                {" = "}Total: <span className="font-mono">{probCheck.grand_total.toFixed(1)}%</span>
                {probCheck.grand_total > 100 && <span className="text-red-600 ml-2">Total exceeds 100% — will be scaled down.</span>}
              </div>
            </div>
          )}
          <div className="rounded-2xl bg-white border border-brand-line p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Scratch Rewards</div>
              <button onClick={() => setRewardForm({ name: "", description: "", type: "coins", value: "10", probability: 5, quantity_cap: 0, expiry_days: 30, is_active: true, sort_order: rewards.length })} className="btn-pill btn-dark text-xs" data-testid="ps-new-reward"><Plus size={11} /> New Reward</button>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">
                <tr>
                  <th className="text-left py-2">Name</th>
                  <th className="text-left py-2">Type / Value</th>
                  <th className="text-left py-2">Probability</th>
                  <th className="text-left py-2">Quantity</th>
                  <th className="text-left py-2">Expiry</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rewards.map((r) => (
                  <tr key={r.id} className="border-t border-brand-line" data-testid={`ps-reward-${r.id}`}>
                    <td className="py-2">
                      <div className="font-medium">{r.name}</div>
                      {r.description && <div className="text-xs text-brand-charcoal/60">{r.description}</div>}
                    </td>
                    <td className="py-2 text-xs"><span className="capitalize">{r.type}</span>{r.value ? ` · ${r.value}` : ""}</td>
                    <td className="py-2 font-mono text-xs">{r.probability}%</td>
                    <td className="py-2 text-xs">{r.quantity_cap ? `${r.quantity_issued || 0}/${r.quantity_cap}` : "Unlimited"}</td>
                    <td className="py-2 text-xs">{r.expiry_days} days</td>
                    <td className="py-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${r.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{r.is_active ? "Active" : "Off"}</span>
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => setRewardForm({ ...r })} className="text-xs mr-2 text-brand-charcoal/70 hover:text-brand-charcoal" data-testid={`ps-edit-reward-${r.id}`}><Edit3 size={10} className="inline" /> Edit</button>
                      <button onClick={() => delReward(r)} className="text-xs text-red-600" data-testid={`ps-delete-reward-${r.id}`}><Trash2 size={10} className="inline" /> Delete</button>
                    </td>
                  </tr>
                ))}
                {rewards.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-brand-charcoal/50">No rewards yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "payments" && (
        <div className="rounded-2xl bg-white border border-brand-line p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">All payments</div>
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/45" />
              <input value={q} onChange={(e) => setQ(e.target.value)} className="pp-input pl-8" placeholder="Search receipt / txn / phone / outlet" data-testid="ps-payments-search" />
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">
              <tr>
                <th className="text-left py-2">Date</th>
                <th className="text-left py-2">Receipt / Txn</th>
                <th className="text-left py-2">Customer</th>
                <th className="text-left py-2">Outlet</th>
                <th className="text-left py-2">Amount</th>
                <th className="text-left py-2">Reward</th>
                <th className="text-left py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.length === 0 ? (
                <tr><td colSpan={7} className="py-8 text-center text-brand-charcoal/50">No payments yet.</td></tr>
              ) : filteredPayments.map((p) => {
                const sc = p.scratch_card;
                return (
                  <tr key={p.id} className="border-t border-brand-line" data-testid={`ps-payment-${p.id}`}>
                    <td className="py-2 text-xs">{p.paid_at ? new Date(p.paid_at).toLocaleString() : "—"}</td>
                    <td className="py-2 font-mono text-xs">
                      <div>{p.receipt_number || "—"}</div>
                      <div className="text-brand-charcoal/50">{(p.razorpay_payment_id || "").slice(-12)}</div>
                    </td>
                    <td className="py-2 text-xs">
                      <div>{p.customer_name || (p.member_code ? p.member_code : "Guest")}</div>
                      {p.customer_phone && <div className="text-brand-charcoal/50">{p.customer_phone}</div>}
                    </td>
                    <td className="py-2 text-xs">{p.outlet_name}</td>
                    <td className="py-2 font-mono">₹{Number(p.amount).toFixed(2)}</td>
                    <td className="py-2 text-xs">
                      {sc?.reward_snapshot?.is_win ? sc.reward_snapshot.name : sc ? "Better luck" : "—"}
                    </td>
                    <td className="py-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "settings" && (
        <div className="rounded-2xl bg-white border border-brand-line p-5 max-w-2xl">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/55">Feature Settings</div>
            <button onClick={saveConfig} className="btn-pill btn-dark text-xs" data-testid="ps-save-settings"><Save size={11} /> Save</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Header title</label>
              <input value={config.header_title} onChange={(e) => setConfig({ ...config, header_title: e.target.value })} className="pp-input" data-testid="ps-cfg-title" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Header subtitle</label>
              <input value={config.header_subtitle} onChange={(e) => setConfig({ ...config, header_subtitle: e.target.value })} className="pp-input" data-testid="ps-cfg-subtitle" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Better Luck probability (%)</label>
              <input type="number" step="0.1" value={config.better_luck_probability} onChange={(e) => setConfig({ ...config, better_luck_probability: e.target.value })} className="pp-input" data-testid="ps-cfg-luck-prob" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Better Luck message</label>
              <input value={config.better_luck_message} onChange={(e) => setConfig({ ...config, better_luck_message: e.target.value })} className="pp-input" data-testid="ps-cfg-luck-msg" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Guest TTL (days)</label>
              <input type="number" value={config.guest_token_ttl_days} onChange={(e) => setConfig({ ...config, guest_token_ttl_days: e.target.value })} className="pp-input" data-testid="ps-cfg-ttl" />
            </div>
          </div>
          <div className="mt-4 flex gap-4 flex-wrap">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!config.is_enabled} onChange={(e) => setConfig({ ...config, is_enabled: e.target.checked })} data-testid="ps-cfg-enabled" /> Feature enabled
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!config.coin_credit_on_win} onChange={(e) => setConfig({ ...config, coin_credit_on_win: e.target.checked })} data-testid="ps-cfg-auto-coins" /> Auto-credit Pop Coins on coin rewards
            </label>
          </div>
        </div>
      )}

      {/* Outlet form modal */}
      {outletForm && (
        <Modal onClose={() => setOutletForm(null)} title={outletForm.id ? "Edit Outlet" : "New Outlet"} testId="ps-outlet-modal">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Name</label><input value={outletForm.name} onChange={(e) => setOutletForm({ ...outletForm, name: e.target.value })} className="pp-input" data-testid="ps-outlet-name" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Code (slug)</label><input value={outletForm.code} onChange={(e) => setOutletForm({ ...outletForm, code: e.target.value })} className="pp-input" data-testid="ps-outlet-code" /></div>
            <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Address</label><input value={outletForm.address || ""} onChange={(e) => setOutletForm({ ...outletForm, address: e.target.value })} className="pp-input" data-testid="ps-outlet-address" /></div>
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!outletForm.is_active} onChange={(e) => setOutletForm({ ...outletForm, is_active: e.target.checked })} /> Active</label>
          </div>
          <ModalActions onCancel={() => setOutletForm(null)} onSave={saveOutlet} testIdSave="ps-outlet-save" />
        </Modal>
      )}

      {/* Reward form modal */}
      {rewardForm && (
        <Modal onClose={() => setRewardForm(null)} title={rewardForm.id ? "Edit Reward" : "New Reward"} testId="ps-reward-modal">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Name</label><input value={rewardForm.name} onChange={(e) => setRewardForm({ ...rewardForm, name: e.target.value })} className="pp-input" data-testid="ps-reward-name" /></div>
            <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Description</label><textarea rows={2} value={rewardForm.description || ""} onChange={(e) => setRewardForm({ ...rewardForm, description: e.target.value })} className="pp-input" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Type</label>
              <select value={rewardForm.type} onChange={(e) => setRewardForm({ ...rewardForm, type: e.target.value })} className="pp-input" data-testid="ps-reward-type">
                {REWARD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Value</label><input value={rewardForm.value || ""} onChange={(e) => setRewardForm({ ...rewardForm, value: e.target.value })} className="pp-input" placeholder='e.g. "10" or "20%"' data-testid="ps-reward-value" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Probability (%)</label><input type="number" step="0.1" value={rewardForm.probability} onChange={(e) => setRewardForm({ ...rewardForm, probability: e.target.value })} className="pp-input" data-testid="ps-reward-prob" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Quantity cap (0 = unlimited)</label><input type="number" value={rewardForm.quantity_cap} onChange={(e) => setRewardForm({ ...rewardForm, quantity_cap: e.target.value })} className="pp-input" data-testid="ps-reward-cap" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Expiry days</label><input type="number" value={rewardForm.expiry_days} onChange={(e) => setRewardForm({ ...rewardForm, expiry_days: e.target.value })} className="pp-input" data-testid="ps-reward-expiry" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Campaign start (optional)</label><input type="datetime-local" value={rewardForm.campaign_start || ""} onChange={(e) => setRewardForm({ ...rewardForm, campaign_start: e.target.value })} className="pp-input" /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Campaign end (optional)</label><input type="datetime-local" value={rewardForm.campaign_end || ""} onChange={(e) => setRewardForm({ ...rewardForm, campaign_end: e.target.value })} className="pp-input" /></div>
            <div className="sm:col-span-2"><label className="text-[10px] uppercase tracking-widest text-brand-charcoal/55">Image URL (optional)</label><input value={rewardForm.image_url || ""} onChange={(e) => setRewardForm({ ...rewardForm, image_url: e.target.value })} className="pp-input" /></div>
            <label className="inline-flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={!!rewardForm.is_active} onChange={(e) => setRewardForm({ ...rewardForm, is_active: e.target.checked })} data-testid="ps-reward-active" /> Active</label>
          </div>
          <ModalActions onCancel={() => setRewardForm(null)} onSave={saveReward} testIdSave="ps-reward-save" />
        </Modal>
      )}
    </div>
  );
}

/* ------- Helpers ------- */
function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "bg-brand-charcoal text-brand-cream border-brand-charcoal" : "bg-white border-brand-line"}`}>
      <div className={`text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${accent ? "text-brand-yellow" : "text-brand-charcoal/55"}`}><Icon size={11} /> {label}</div>
      <div className="text-2xl font-display mt-1">{value ?? 0}</div>
    </div>
  );
}

function Modal({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div className="max-w-2xl w-full bg-white rounded-2xl p-6 overflow-y-auto max-h-[92vh]" onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-2xl">{title}</div>
          <button onClick={onClose} className="text-brand-charcoal/60 hover:text-brand-charcoal">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
function ModalActions({ onCancel, onSave, testIdSave }) {
  return (
    <div className="mt-6 flex justify-end gap-2">
      <button onClick={onCancel} className="btn-pill btn-ghost text-xs">Cancel</button>
      <button onClick={onSave} className="btn-pill btn-dark text-xs" data-testid={testIdSave}><Save size={11} /> Save</button>
    </div>
  );
}
